// ----------------------------------------------------------------------- imports externos
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ----------------------------------------------------------------------- imports internos
import { useAuth } from "../auth/AuthContext";
import {
  alLlegarMensaje,
  guardarMiContacto,
  mandarMensaje,
  marcarLeidos,
  marcarPendiente,
  marcarRecibidos,
  mensajeATicket,
  traerConversaciones,
  traerHilo,
  traerLibreta,
} from "../services/mensajes";
import { traerAreas } from "../services/tickets";
import Loading from "./Loading";
import { diaAr, diaHoraAr } from "../shared/fechaAr";
import { cuandoLlego } from "../shared/facturasRemitos";

/* Mensajes internos y libreta.
 *
 * EL PROBLEMA QUE RESUELVE: alguien necesita una respuesta de otro antes de
 * poder seguir. Hoy camina hasta el escritorio o marca el interno, y las dos
 * cosas exigen que los DOS estén libres en el mismo instante. Si el otro está
 * ocupado, la pregunta queda en el aire; si se desocupa dos minutos después, se
 * pierden los ocho que faltan hasta volver a intentarlo.
 *
 * Un mensaje no exige simultaneidad. Eso es todo lo que hace esto.
 *
 * La libreta no es un accesorio del chat: el interno del teléfono es lo que se
 * marca hoy, así que sirve incluso para quien prefiera seguir llamando.
 */

const nombreCorto = (mail) => (mail || "").split("@")[0] || "—";

function cuando(iso) {
  const t = new Date(iso);
  const min = Math.floor((Date.now() - t.getTime()) / 60000);
  if (min < 1) return "recién";
  if (min < 60) return `${min} min`;
  if (min < 60 * 24) return `${Math.floor(min / 60)} h`;
  if (min < 60 * 24 * 7) return `${Math.floor(min / 1440)} d`;
  return diaAr(iso); // en día argentino, no en el de la máquina
}

/* ✓ salió · ✓✓ le llegó a la pantalla · ✓✓ en color, lo abrió.
 *
 * El del medio es el que más informa y el que menos se piensa: dice "todavía
 * no lo tiene", y eso es lo que uno necesita saber antes de decidir si igual
 * levanta el teléfono. */
const Tildes = ({ m }) => {
  const estado = m.leido_en ? "leido" : m.recibido_en ? "recibido" : "enviado";
  const dice = {
    enviado: "Enviado",
    recibido: "Le llegó, todavía no lo abrió",
    leido: "Lo leyó",
  }[estado];
  return (
    <span className={"msg-tildes " + estado} title={dice} aria-label={dice}>
      {estado === "enviado" ? "✓" : "✓✓"}
    </span>
  );
};

/* El día y la hora de cada mensaje: «hoy 14:32», «ayer 09:10», «23/10 14:32».
 *
 * Nico, 24/10 (ticket 508): «los mensajes sabés la hora pero no el día». La
 * burbuja decía sólo «14:32», y en un hilo de varios días no había forma de
 * saber si eso era de hoy o de la semana pasada. Es el mismo formato —y la
 * misma función— que el pie de las cartas de Facturas y remitos: hoy y ayer
 * son los de Buenos Aires, no los de la máquina del que mira. */
const hora = (iso) => cuandoLlego(iso);

// ------------------------------------------------------------------ mi ficha
const MiFicha = ({ yo, mio, onGuardado }) => {
  const [interno, setInterno] = useState(mio?.interno || "");
  const [celular, setCelular] = useState(mio?.celular || "");
  const [nota, setNota] = useState(mio?.nota || "");
  const [estado, setEstado] = useState(null);

  const guardar = async (e) => {
    e.preventDefault();
    setEstado("guardando");
    try {
      await guardarMiContacto({ mail: yo, interno, celular, nota });
      setEstado("listo");
      onGuardado();
    } catch (err) {
      setEstado(err.message || "No se pudo guardar");
    }
  };

  return (
    <form className="tk-nuevo" onSubmit={guardar}>
      <h2>Tus datos en la libreta</h2>
      <p className="tk-meta">
        Esto lo ve el resto cuando te busca. El interno es lo que más se usa.
      </p>
      <div className="tk-campos">
        <label className="tk-campo">
          <span>Interno</span>
          <input
            value={interno}
            onChange={(e) => setInterno(e.target.value)}
            placeholder="105"
          />
        </label>
        <label className="tk-campo">
          <span>Celular</span>
          <input
            value={celular}
            onChange={(e) => setCelular(e.target.value)}
            placeholder="11 5555 5555"
          />
        </label>
      </div>
      <label className="tk-campo">
        <span>Nota (opcional)</span>
        <input
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Los martes estoy en el taller"
        />
      </label>
      <div className="tk-acciones">
        <button className="tk-boton tk-boton--fuerte" disabled={estado === "guardando"}>
          {estado === "guardando" ? "Guardando…" : "Guardar"}
        </button>
      </div>
      {estado && estado !== "guardando" && (
        <div className={"hub-aviso " + (estado === "listo" ? "ok" : "error")} role="status">
          {estado === "listo" ? "Guardado." : estado}
        </div>
      )}
    </form>
  );
};

// ------------------------------------------------------------------- libreta
const Ficha = ({ p, onEscribir }) => (
  <section className="tk-detalle">
    <div className="tk-cabeza">
      <div style={{ minWidth: 0 }}>
        <h2>{p.nombre || nombreCorto(p.mail)}</h2>
        <p className="tk-meta">
          {[p.rol, p.area, p.sucursal].filter(Boolean).join(" · ") ||
            "sin área cargada"}
        </p>
      </div>
    </div>

    <div className="msg-datos">
      {/* Los teléfonos son enlaces: en el celular llaman de una, y en la
          computadora al menos se pueden copiar sin transcribir a mano. */}
      {p.interno && (
        <div>
          <span className="et">Interno</span>
          <a href={`tel:${p.interno}`}>{p.interno}</a>
        </div>
      )}
      {p.celular && (
        <div>
          <span className="et">Celular</span>
          <a href={`tel:${p.celular.replace(/\s/g, "")}`}>{p.celular}</a>
        </div>
      )}
      <div>
        <span className="et">Correo</span>
        <a href={`mailto:${p.mail}`}>{p.mail}</a>
      </div>
      {p.nota && (
        <div>
          <span className="et">Nota</span>
          <span>{p.nota}</span>
        </div>
      )}
      {!p.interno && !p.celular && (
        <p className="tk-vacio">
          Todavía no cargó su interno. Puede hacerlo desde “Mis datos”.
        </p>
      )}
    </div>

    <div className="tk-acciones">
      <button className="tk-boton tk-boton--fuerte" onClick={() => onEscribir(p.mail)}>
        Escribirle
      </button>
    </div>
  </section>
);

// ---------------------------------------------------------------------- hilo
const Hilo = ({ con, quien, yo, areas, refrescar }) => {
  const [mensajes, setMensajes] = useState(null);
  const [texto, setTexto] = useState("");
  const [error, setError] = useState(null);
  const fondo = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const m = await traerHilo(con);
      setMensajes(m);
      // Marcar leído al abrir, no al recibir: "visto" tiene que significar que
      // alguien lo miró de verdad.
      if (m.some((x) => x.para === yo && !x.leido_en)) {
        await marcarLeidos(con);
        refrescar();
      }
    } catch (e) {
      setError(e.message || "No se pudo abrir la conversación");
    }
  }, [con, yo, refrescar]);

  const anotar = async (m) => {
    try {
      await marcarPendiente(m.id, !m.pendiente_en);
      await cargar();
      refrescar();
    } catch (e) {
      setError(e.message || "No se pudo anotar");
    }
  };

  const aTicket = async (m, area) => {
    try {
      await mensajeATicket(m.id, area);
      await cargar();
      refrescar();
    } catch (e) {
      setError(e.message || "No se pudo pasar a ticket");
    }
  };

  useEffect(() => {
    setMensajes(null);
    cargar();
  }, [cargar]);

  // Al fondo, como cualquier chat: lo último es lo que importa.
  useEffect(() => {
    fondo.current?.scrollIntoView({ block: "end" });
  }, [mensajes]);

  useEffect(() => alLlegarMensaje((m) => {
    if (m.de === con || m.para === con) cargar();
  }), [con, cargar]);

  const enviar = async (e) => {
    e.preventDefault();
    const cuerpo = texto.trim();
    if (!cuerpo) return;
    setTexto("");
    setError(null);
    try {
      await mandarMensaje(con, cuerpo);
      await cargar();
      refrescar();
    } catch (err) {
      setError(err.message || "No se pudo enviar");
      setTexto(cuerpo); // No se pierde lo escrito por un error de red.
    }
  };

  return (
    <section className="tk-detalle msg-hilo-marco">
      <div className="tk-cabeza">
        <div style={{ minWidth: 0 }}>
          <h2>{quien?.nombre || nombreCorto(con)}</h2>
          <p className="tk-meta">
            {[quien?.area, quien?.interno && `interno ${quien.interno}`]
              .filter(Boolean)
              .join(" · ") || con}
          </p>
        </div>
      </div>

      <div className="msg-hilo">
        {mensajes === null && <p className="tk-vacio">Cargando…</p>}
        {mensajes?.length === 0 && (
          <p className="tk-vacio">
            Todavía no se escribieron. Escribí lo que necesitás saber y seguí
            con lo tuyo — te contesta cuando pueda.
          </p>
        )}
        {mensajes?.map((m) => (
          <div
            key={m.id}
            className={
              "msg-burbuja" +
              (m.de === yo ? " mia" : "") +
              (m.pendiente_en ? " anotada" : "")
            }
          >
            <span className="msg-texto">{m.cuerpo}</span>
            <span className="msg-hora" title={diaHoraAr(m.creado_en)}>
              {hora(m.creado_en)}
              {m.de === yo && <Tildes m={m} />}
            </span>

            {/* Sólo en lo que uno RECIBIÓ: anotarse algo es memoria propia, y
                dejar que el que pide marque pendientes en la bandeja ajena es
                reinventar al jefe que llena la lista de otro. */}
            {m.para === yo && (
              <span className="msg-acciones">
                <button onClick={() => anotar(m)}>
                  {m.pendiente_en ? "✓ anotado — sacar" : "Anotar"}
                </button>
                {m.ticket ? (
                  <span className="msg-ficha">→ ticket #{m.ticket}</span>
                ) : (
                  <select
                    defaultValue=""
                    aria-label="Pasar a ticket"
                    onChange={(e) => e.target.value && aTicket(m, e.target.value)}
                  >
                    <option value="">Pasar a ticket…</option>
                    {areas.map((a) => (
                      <option key={a.clave} value={a.clave}>
                        {a.etiqueta}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            )}
          </div>
        ))}
        <div ref={fondo} />
      </div>

      {error && (
        <div className="hub-aviso error" role="status">
          {error}
        </div>
      )}

      <form className="tk-responder" onSubmit={enviar}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={`Escribirle a ${nombreCorto(con)}…`}
          aria-label="Escribir un mensaje"
          autoFocus
        />
        <button className="tk-boton" disabled={!texto.trim()}>
          Enviar
        </button>
      </form>
    </section>
  );
};

// -------------------------------------------------------------------- pantalla
const Mensajes = () => {
  const { user } = useAuth();
  const yo = (user?.email || "").toLowerCase();

  const [libreta, setLibreta] = useState(null);
  const [areas, setAreas] = useState([]);
  const [convers, setConvers] = useState([]);
  const [error, setError] = useState(null);
  const [abierta, setAbierta] = useState(null);
  const [vista, setVista] = useState("chats");
  const [busqueda, setBusqueda] = useState("");
  const [misDatos, setMisDatos] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([traerLibreta(), traerConversaciones()]);
      setLibreta(l);
      setConvers(c);
    } catch (e) {
      setError(e.message || "No se pudo cargar");
    }
  }, []);

  useEffect(() => {
    cargar();
    traerAreas().then(setAreas).catch(() => setAreas([]));
    // Al abrir la pantalla, todo lo que llegó pasa a "recibido". Es el momento
    // honesto: recién ahí el mensaje está de verdad en la pantalla de alguien.
    marcarRecibidos().catch(() => {});
  }, [cargar]);

  // Un mensaje nuevo tiene que mover la lista aunque uno esté en otra
  // conversación: si no, el aviso aparece recién al recargar.
  useEffect(
    () =>
      alLlegarMensaje((m) => {
        if (m.para === yo) marcarRecibidos().catch(() => {});
        cargar();
      }),
    [cargar, yo]
  );

  const porMail = useMemo(() => {
    const m = {};
    (libreta || []).forEach((p) => (m[p.mail] = p));
    return m;
  }, [libreta]);

  const gente = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (libreta || [])
      .filter((p) => p.mail !== yo)
      .filter(
        (p) =>
          !q ||
          (p.nombre || "").toLowerCase().includes(q) ||
          p.mail.includes(q) ||
          (p.area || "").toLowerCase().includes(q) ||
          (p.interno || "").includes(q)
      );
  }, [libreta, busqueda, yo]);

  const abrir = (mail) => {
    setAbierta(mail);
    setMisDatos(false);
  };

  if (error && !libreta) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }
  if (!libreta) return <Loading />;

  const lista =
    vista === "chats"
      ? convers.map((c) => ({
          mail: c.con,
          sub: (c.ultimo_mio ? "vos: " : "") + c.ultimo,
          cuando: cuando(c.cuando),
          sinLeer: c.sin_leer,
          pendientes: c.pendientes,
        }))
      : gente.map((p) => ({
          mail: p.mail,
          sub: [p.area, p.interno && `int. ${p.interno}`]
            .filter(Boolean)
            .join(" · "),
          sinLeer: 0,
        }));

  return (
    <div className="tk">
      <aside className="tk-lista">
        <div className="tk-filtros">
          <button
            className={"tk-chip" + (vista === "chats" ? " activo" : "")}
            onClick={() => setVista("chats")}
          >
            Conversaciones
          </button>
          <button
            className={"tk-chip" + (vista === "libreta" ? " activo" : "")}
            onClick={() => setVista("libreta")}
          >
            Libreta
          </button>
          <button
            className={"tk-chip" + (misDatos ? " activo" : "")}
            onClick={() => {
              setMisDatos(true);
              setAbierta(null);
            }}
          >
            Mis datos
          </button>
        </div>

        {vista === "libreta" && (
          <input
            className="permisos-buscar"
            type="search"
            placeholder="Buscar por nombre, área o interno…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            aria-label="Buscar en la libreta"
          />
        )}

        {lista.map((x) => (
          <button
            key={x.mail}
            className={"tk-item" + (x.mail === abierta ? " activa" : "")}
            onClick={() => abrir(x.mail)}
          >
            <span className="tk-item-txt">
              <span className="tk-item-tit">
                {porMail[x.mail]?.nombre || nombreCorto(x.mail)}
                {x.sinLeer > 0 && <span className="msg-nuevos">{x.sinLeer}</span>}
                {/* Se muestra aunque esté todo leído. Es la diferencia entre
                    "no lo miré" y "lo miré y todavía lo debo". */}
                {x.pendientes > 0 && (
                  <span className="msg-anotados" title="Anotado para no olvidarte">
                    {x.pendientes} anotado{x.pendientes > 1 ? "s" : ""}
                  </span>
                )}
              </span>
              <span className="tk-item-meta">
                {x.sub}
                {x.cuando && ` · ${x.cuando}`}
              </span>
            </span>
          </button>
        ))}

        {lista.length === 0 && (
          <p className="tk-vacio">
            {vista === "chats"
              ? "Todavía no hablaste con nadie. Buscá a alguien en la libreta."
              : "Nadie coincide con esa búsqueda."}
          </p>
        )}
      </aside>

      {misDatos ? (
        <MiFicha
          yo={yo}
          mio={porMail[yo]}
          onGuardado={cargar}
        />
      ) : abierta ? (
        vista === "libreta" && !convers.some((c) => c.con === abierta) ? (
          <Ficha p={porMail[abierta] || { mail: abierta }} onEscribir={(m) => {
            setVista("chats");
            setAbierta(m);
          }} />
        ) : (
          <Hilo
            key={abierta}
            con={abierta}
            quien={porMail[abierta]}
            yo={yo}
            areas={areas}
            refrescar={cargar}
          />
        )
      ) : (
        <section className="tk-detalle tk-detalle--vacio">
          <p>
            Elegí a alguien de la izquierda. Un mensaje no obliga a que los dos
            estén libres al mismo tiempo, que es lo que pasa con el teléfono.
          </p>
        </section>
      )}
    </div>
  );
};

export default Mensajes;
